/**
 * AddItem — Shopify-style item form (create / edit).
 *
 * Sections:
 *   • Basics: title, description, images (drag-drop upload), category
 *   • Pricing: base price + compare-at
 *   • Variants: option names (Size/Color…) + value matrix with
 *     per-variant price + inventory + SKU
 *   • Inventory: track_inventory toggle, simple qty when no variants
 *   • Shipping: weight/dims, flat rate, free_shipping toggle
 *
 * Images are uploaded immediately on drop → object storage URL is
 * returned and stored in `images[]`. Save button saves the entire doc.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { X, Plus, Upload, Loader2, Trash2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { GlassCard, API } from './GlassCard';

function abs(url) {
  if (!url) return '';
  return url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
}

const DEFAULT = {
  title: '', description: '',
  category_id: '', subcategory_id: '',
  base_price: '', compare_at_price: '',
  images: [],
  tags: [],
  option_names: [],   // e.g. ['Size', 'Color']
  variants: [],
  track_inventory: true,
  inventory: 0,
  shipping_weight_oz: '', shipping_length_in: '', shipping_width_in: '', shipping_height_in: '',
  flat_shipping_rate: '', free_shipping: false,
  is_active: true,
};

/* Build the cartesian product of option values -> variant templates */
function buildVariantMatrix(optionNames, optionValues) {
  if (optionNames.length === 0) return [];
  const combos = [[]];
  for (const name of optionNames) {
    const vals = optionValues[name] || [];
    const next = [];
    for (const c of combos) {
      for (const v of vals) next.push([...c, [name, v]]);
    }
    if (next.length === 0) return [];
    combos.length = 0;
    combos.push(...next);
  }
  return combos.map((pairs) => ({
    option_values: Object.fromEntries(pairs),
    sku: '',
    price: null,
    inventory: 0,
    image_url: null,
  }));
}

export default function AddItem({ existing, onCancel, onSaved }) {
  const { getAuthHeaders } = useAuth();
  const [form, setForm] = useState(() => existing ? { ...DEFAULT, ...existing } : { ...DEFAULT });
  const [categories, setCategories] = useState([]);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  // Keep an "option values" structure that drives the matrix.
  const [optionValues, setOptionValues] = useState(() => {
    const map = {};
    (existing?.option_names || []).forEach((name) => {
      const vals = Array.from(new Set(
        (existing.variants || []).map((v) => v.option_values?.[name]).filter(Boolean),
      ));
      map[name] = vals;
    });
    return map;
  });

  // Load categories once.
  useEffect(() => {
    fetch(`${API}/categories/`).then((r) => r.json()).then(setCategories).catch(() => setCategories([]));
  }, []);

  // Whenever option_names / optionValues change, regenerate the variant
  // matrix, preserving existing values when the signature matches.
  useEffect(() => {
    if (form.option_names.length === 0) {
      setForm((f) => ({ ...f, variants: [] }));
      return;
    }
    setForm((f) => {
      const newMatrix = buildVariantMatrix(f.option_names, optionValues);
      const merged = newMatrix.map((template) => {
        const existing = (f.variants || []).find((v) =>
          f.option_names.every((n) => (v.option_values || {})[n] === template.option_values[n]),
        );
        return existing ? { ...template, ...existing, option_values: template.option_values } : template;
      });
      return { ...f, variants: merged };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.option_names, optionValues]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  // ─── Image upload (deferred: must save first to get item_id, OR we
  // create a placeholder and get the id) ────────────────────────────
  const uploadImage = useCallback(async (file) => {
    // We need an item_id to upload. If this is a new item, create a draft
    // row first (title placeholder) so we have one.
    let itemId = existing?.item_id;
    if (!itemId) {
      const r = await fetch(`${API}/items/`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, title: form.title || 'Untitled draft', base_price: Number(form.base_price) || 0, is_active: false }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Could not create draft');
      }
      const data = await r.json();
      itemId = data.item_id;
      // Patch our local state so subsequent uploads reuse the same id.
      update({ item_id: itemId });
    }
    const fd = new FormData();
    fd.append('file', file);
    const r = await fetch(`${API}/items/${itemId}/upload-image`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: fd,
    });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      throw new Error(d.detail || 'Upload failed');
    }
    const { url } = await r.json();
    return url;
  }, [existing?.item_id, form, getAuthHeaders]);

  const onPickFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploadingImg(true);
    try {
      for (const f of files) {
        if (form.images.length >= 10) break;
        const url = await uploadImage(f);
        setForm((prev) => ({ ...prev, images: [...prev.images, url] }));
      }
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.message || 'Image upload failed');
    } finally {
      setUploadingImg(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removeImage = (i) => update({ images: form.images.filter((_, idx) => idx !== i) });

  // ─── Options & variants management ──────────────────────────────
  const addOptionName = () => {
    const next = window.prompt('Option name (e.g. Size, Color, Material)');
    if (!next) return;
    const clean = next.trim();
    if (!clean || form.option_names.includes(clean) || form.option_names.length >= 3) return;
    update({ option_names: [...form.option_names, clean] });
    setOptionValues((v) => ({ ...v, [clean]: [] }));
  };
  const removeOptionName = (name) => {
    update({ option_names: form.option_names.filter((n) => n !== name) });
    setOptionValues((v) => { const n = { ...v }; delete n[name]; return n; });
  };
  const addOptionValue = (name) => {
    const next = window.prompt(`Add a value for "${name}"`);
    if (!next) return;
    const clean = next.trim();
    if (!clean) return;
    setOptionValues((v) => ({ ...v, [name]: [...new Set([...(v[name] || []), clean])] }));
  };
  const removeOptionValue = (name, val) => {
    setOptionValues((v) => ({ ...v, [name]: (v[name] || []).filter((x) => x !== val) }));
  };
  const updateVariant = (i, patch) => {
    setForm((f) => ({
      ...f,
      variants: f.variants.map((v, idx) => idx === i ? { ...v, ...patch } : v),
    }));
  };

  // ─── Save ──────────────────────────────────────────────────────
  const save = async () => {
    if (!form.title.trim()) { toast.error('Please add a title'); return; }
    if (!form.base_price || Number(form.base_price) <= 0) { toast.error('Base price must be greater than $0'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        base_price: Number(form.base_price),
        compare_at_price: form.compare_at_price ? Number(form.compare_at_price) : null,
        inventory: Number(form.inventory) || 0,
        shipping_weight_oz: form.shipping_weight_oz ? Number(form.shipping_weight_oz) : null,
        shipping_length_in: form.shipping_length_in ? Number(form.shipping_length_in) : null,
        shipping_width_in: form.shipping_width_in ? Number(form.shipping_width_in) : null,
        shipping_height_in: form.shipping_height_in ? Number(form.shipping_height_in) : null,
        flat_shipping_rate: form.flat_shipping_rate ? Number(form.flat_shipping_rate) : null,
        variants: (form.variants || []).map((v) => ({
          ...v,
          price: v.price != null && v.price !== '' ? Number(v.price) : null,
          inventory: Number(v.inventory) || 0,
        })),
      };
      const method = (form.item_id || existing?.item_id) ? 'PUT' : 'POST';
      const url = (form.item_id || existing?.item_id)
        ? `${API}/items/${form.item_id || existing.item_id}`
        : `${API}/items/`;
      const r = await fetch(url, {
        method,
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Save failed');
      }
      toast.success(existing ? 'Item updated' : 'Item created');
      onSaved?.();
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="add-item-form">
      <div className="flex items-center justify-between">
        <div>
          <button onClick={onCancel} className="inline-flex items-center gap-1.5 text-xs text-[#9CA3AF] hover:text-white mb-2">
            <ArrowLeft size={12} /> Back to items
          </button>
          <h1 className="text-2xl sm:text-3xl font-bold text-white font-syne">
            {existing ? 'Edit item' : 'Add a new item'}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onCancel} className="px-4 py-2 rounded-full text-sm font-semibold bg-white/5 border border-white/10 text-[#C4B5FD] hover:bg-white/10">
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="px-5 py-2 rounded-full text-sm font-bold text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] hover:shadow-[0_6px_20px_rgba(139,92,246,0.4)] disabled:opacity-50"
            data-testid="add-item-save"
          >
            {saving ? 'Saving…' : (existing ? 'Save changes' : 'Save item')}
          </button>
        </div>
      </div>

      {/* ─── Basics ─────────────────────────────────────────────── */}
      <GlassCard className="p-5 space-y-4">
        <p className="text-xs uppercase tracking-wider text-[#9CA3AF] font-semibold">Basics</p>
        <input
          value={form.title}
          onChange={(e) => update({ title: e.target.value })}
          placeholder="Item title (e.g. Hand-beaded earrings)"
          className="w-full h-11 px-3.5 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder:text-[#9CA3AF] focus:outline-none focus:border-[#8B5CF6]"
          data-testid="add-item-title"
        />
        <textarea
          value={form.description}
          onChange={(e) => update({ description: e.target.value })}
          placeholder="Describe the materials, size, inspiration, care…"
          rows={5}
          className="w-full px-3.5 py-3 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder:text-[#9CA3AF] focus:outline-none focus:border-[#8B5CF6] resize-none"
          data-testid="add-item-description"
        />
        <select
          value={form.category_id || ''}
          onChange={(e) => update({ category_id: e.target.value || null })}
          className="w-full h-11 px-3 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:border-[#8B5CF6]"
          data-testid="add-item-category"
        >
          <option value="">— Pick a category —</option>
          {(categories || []).map((c) => (
            <option key={c.id} value={c.id}>{c.icon ? `${c.icon} ` : ''}{c.name}</option>
          ))}
        </select>
      </GlassCard>

      {/* ─── Images ─────────────────────────────────────────────── */}
      <GlassCard className="p-5 space-y-4">
        <p className="text-xs uppercase tracking-wider text-[#9CA3AF] font-semibold">Images (up to 10)</p>
        <div className="grid grid-cols-5 gap-3" data-testid="add-item-images">
          {form.images.map((img, i) => (
            <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-white/10 bg-[#1A1D4A]">
              <img src={abs(img)} alt="" className="w-full h-full object-cover" />
              <button
                onClick={() => removeImage(i)}
                className="absolute top-1 right-1 p-1 rounded-full bg-black/70 hover:bg-red-500/80 text-white"
                data-testid={`add-item-image-remove-${i}`}
              >
                <X size={12} />
              </button>
            </div>
          ))}
          {form.images.length < 10 && (
            <label className="aspect-square flex items-center justify-center rounded-xl border-2 border-dashed border-white/15 text-[#9CA3AF] cursor-pointer hover:border-[#8B5CF6] hover:text-white transition-all">
              {uploadingImg ? <Loader2 size={18} className="animate-spin" /> : (
                <div className="flex flex-col items-center gap-1 text-[11px]">
                  <Upload size={16} />
                  <span>Add image</span>
                </div>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                onChange={onPickFile}
                className="hidden"
                data-testid="add-item-image-input"
              />
            </label>
          )}
        </div>
      </GlassCard>

      {/* ─── Pricing ────────────────────────────────────────────── */}
      <GlassCard className="p-5 space-y-4">
        <p className="text-xs uppercase tracking-wider text-[#9CA3AF] font-semibold">Pricing</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-[#9CA3AF] mb-1 block">Base price (USD)</label>
            <input
              type="number"
              min="0.5"
              step="0.01"
              value={form.base_price}
              onChange={(e) => update({ base_price: e.target.value })}
              placeholder="0.00"
              className="w-full h-11 px-3.5 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:border-[#8B5CF6]"
              data-testid="add-item-price"
            />
          </div>
          <div>
            <label className="text-xs text-[#9CA3AF] mb-1 block">Compare-at price (strikethrough, optional)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.compare_at_price}
              onChange={(e) => update({ compare_at_price: e.target.value })}
              placeholder="0.00"
              className="w-full h-11 px-3.5 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:border-[#8B5CF6]"
              data-testid="add-item-compare-price"
            />
          </div>
        </div>
      </GlassCard>

      {/* ─── Variants ───────────────────────────────────────────── */}
      <GlassCard className="p-5 space-y-4" data-testid="add-item-variants-card">
        <div className="flex items-center justify-between">
          <p className="text-xs uppercase tracking-wider text-[#9CA3AF] font-semibold">Variants</p>
          <button
            onClick={addOptionName}
            disabled={form.option_names.length >= 3}
            className="inline-flex items-center gap-1 text-xs text-[#C4B5FD] hover:text-white disabled:opacity-40"
            data-testid="add-option-name"
          >
            <Plus size={12} /> Add option (Size, Color…)
          </button>
        </div>
        {form.option_names.length === 0 && (
          <p className="text-sm text-[#9CA3AF]">No variants yet — the item will be sold as a single SKU.</p>
        )}
        {form.option_names.map((name) => (
          <div key={name} className="space-y-2">
            <div className="flex items-center gap-2">
              <p className="font-semibold text-white text-sm">{name}</p>
              <button onClick={() => removeOptionName(name)} className="text-xs text-red-300 hover:text-red-200">Remove</button>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {(optionValues[name] || []).map((val) => (
                <span key={val} className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-white/10 border border-white/15">
                  {val}
                  <button onClick={() => removeOptionValue(name, val)} className="opacity-70 hover:opacity-100"><X size={10} /></button>
                </span>
              ))}
              <button
                onClick={() => addOptionValue(name)}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold text-[#C4B5FD] border border-dashed border-white/20 hover:bg-white/5"
              >
                <Plus size={10} /> Add value
              </button>
            </div>
          </div>
        ))}

        {form.variants.length > 0 && (
          <div className="mt-4 border border-white/10 rounded-xl overflow-hidden" data-testid="add-item-variant-matrix">
            <div className="grid grid-cols-[1.5fr_1fr_1fr_1fr] px-3 py-2 text-[11px] uppercase tracking-wider text-[#9CA3AF] font-semibold bg-white/5">
              <span>Variant</span>
              <span>Price (opt.)</span>
              <span>Inventory</span>
              <span>SKU</span>
            </div>
            {form.variants.map((v, i) => (
              <div key={i} className="grid grid-cols-[1.5fr_1fr_1fr_1fr] px-3 py-2 border-t border-white/10 items-center text-sm">
                <span className="text-white font-medium">{Object.values(v.option_values || {}).join(' / ')}</span>
                <input
                  type="number"
                  step="0.01"
                  value={v.price ?? ''}
                  onChange={(e) => updateVariant(i, { price: e.target.value })}
                  placeholder="Base"
                  className="w-full h-8 px-2 rounded-md bg-white/5 border border-white/10 text-xs text-white focus:outline-none focus:border-[#8B5CF6]"
                />
                <input
                  type="number"
                  value={v.inventory ?? 0}
                  onChange={(e) => updateVariant(i, { inventory: e.target.value })}
                  className="w-full h-8 px-2 rounded-md bg-white/5 border border-white/10 text-xs text-white focus:outline-none focus:border-[#8B5CF6]"
                />
                <input
                  value={v.sku ?? ''}
                  onChange={(e) => updateVariant(i, { sku: e.target.value })}
                  placeholder="(optional)"
                  className="w-full h-8 px-2 rounded-md bg-white/5 border border-white/10 text-xs text-white focus:outline-none focus:border-[#8B5CF6]"
                />
              </div>
            ))}
          </div>
        )}
      </GlassCard>

      {/* ─── Inventory ─────────────────────────────────────────── */}
      <GlassCard className="p-5 space-y-4">
        <p className="text-xs uppercase tracking-wider text-[#9CA3AF] font-semibold">Inventory</p>
        <label className="inline-flex items-center gap-2 text-sm text-white">
          <input
            type="checkbox"
            checked={form.track_inventory}
            onChange={(e) => update({ track_inventory: e.target.checked })}
            className="accent-[#8B5CF6]"
            data-testid="add-item-track-inventory"
          />
          Track inventory (decrements on sale, hides when out of stock)
        </label>
        {form.option_names.length === 0 && form.track_inventory && (
          <div>
            <label className="text-xs text-[#9CA3AF] mb-1 block">Quantity on hand</label>
            <input
              type="number"
              min="0"
              value={form.inventory}
              onChange={(e) => update({ inventory: e.target.value })}
              className="w-32 h-11 px-3.5 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:border-[#8B5CF6]"
              data-testid="add-item-inventory"
            />
          </div>
        )}
      </GlassCard>

      {/* ─── Shipping ──────────────────────────────────────────── */}
      <GlassCard className="p-5 space-y-4">
        <p className="text-xs uppercase tracking-wider text-[#9CA3AF] font-semibold">Shipping</p>
        <label className="inline-flex items-center gap-2 text-sm text-white">
          <input
            type="checkbox"
            checked={form.free_shipping}
            onChange={(e) => update({ free_shipping: e.target.checked })}
            className="accent-[#8B5CF6]"
            data-testid="add-item-free-shipping"
          />
          Free shipping on this item
        </label>
        {!form.free_shipping && (
          <>
            <div>
              <label className="text-xs text-[#9CA3AF] mb-1 block">Flat shipping rate (USD)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.flat_shipping_rate}
                onChange={(e) => update({ flat_shipping_rate: e.target.value })}
                placeholder="0.00 (used when live carriers aren't configured)"
                className="w-full h-11 px-3.5 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:border-[#8B5CF6]"
                data-testid="add-item-flat-rate"
              />
            </div>
            <div className="grid grid-cols-4 gap-2">
              <input type="number" step="0.1" value={form.shipping_weight_oz} onChange={(e) => update({ shipping_weight_oz: e.target.value })} placeholder="Weight (oz)" className="h-11 px-3 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder:text-[#9CA3AF]" data-testid="add-item-weight" />
              <input type="number" step="0.1" value={form.shipping_length_in} onChange={(e) => update({ shipping_length_in: e.target.value })} placeholder="L (in)" className="h-11 px-3 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder:text-[#9CA3AF]" />
              <input type="number" step="0.1" value={form.shipping_width_in} onChange={(e) => update({ shipping_width_in: e.target.value })} placeholder="W (in)" className="h-11 px-3 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder:text-[#9CA3AF]" />
              <input type="number" step="0.1" value={form.shipping_height_in} onChange={(e) => update({ shipping_height_in: e.target.value })} placeholder="H (in)" className="h-11 px-3 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder:text-[#9CA3AF]" />
            </div>
            <p className="text-[11px] text-[#9CA3AF]">
              Live USPS / UPS / FedEx rates are pulled automatically if you&apos;ve connected a carrier under Payment Settings → Shipping Carriers.
            </p>
          </>
        )}
      </GlassCard>

      <div className="flex items-center justify-end gap-2 pb-2">
        <button onClick={onCancel} className="px-4 py-2 rounded-full text-sm font-semibold bg-white/5 border border-white/10 text-[#C4B5FD] hover:bg-white/10">
          Cancel
        </button>
        <button
          onClick={save}
          disabled={saving}
          className="px-5 py-2 rounded-full text-sm font-bold text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] hover:shadow-[0_6px_20px_rgba(139,92,246,0.4)] disabled:opacity-50"
          data-testid="add-item-save-bottom"
        >
          {saving ? (
            <span className="inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Saving…</span>
          ) : (existing ? 'Save changes' : 'Save item')}
        </button>
      </div>
    </div>
  );
}
